import React, { ComponentProps, ElementType } from 'react'

import { IconProp } from '@fortawesome/fontawesome-svg-core'
import { faTimes } from '@fortawesome/pro-solid-svg-icons'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import {
  Box,
  IconButton,
  ModalProps,
  Drawer as MuiDrawer,
  DrawerProps as MuiDrawerProps,
  Typography,
} from '@material-ui/core'
import { Theme, makeStyles } from '@material-ui/core/styles'

const useStyles = makeStyles<Theme, { bodyPadding?: false; maxWidth?: number }>((theme) => ({
  drawerPaper: {
    width: '100%',
    [theme.breakpoints.up('sm')]: {
      width: '65%',
    },
    [theme.breakpoints.up('md')]: {
      width: ({ maxWidth }) => maxWidth || '45%',
    },
  },
  drawerHeader: {
    display: 'flex',
    alignItems: 'center',
    padding: theme.spacing(0, 2),
    ...theme.mixins.toolbar,
    justifyContent: 'space-between',
    backgroundColor: theme.palette.primary.main,
  },
  icon: {
    color: theme.palette.common.white,
    fontSize: theme.typography.pxToRem(18),
    width: theme.typography.pxToRem(18),
  },
  drawerBody: {
    padding: ({ bodyPadding }) => (bodyPadding === false ? 0 : theme.spacing(2)),
    overflow: 'auto',
    position: 'relative',

    // force height and flexing to allow sticky footer
    height: '100%',
    display: 'flex',
    flexDirection: 'column',
  },
  drawerBackdrop: {
    backgroundColor: 'rgba(0, 0, 0, 0.25)',
  },
  footer: {
    position: 'sticky',
    bottom: 0,
    marginTop: -1, // account for the floating sticky border
    borderTop: `1px solid ${theme.palette.divider}`,
    background: theme.palette.common.white,
    zIndex: 2,
  },
}))

type DrawerProps = Omit<MuiDrawerProps, 'classes' | 'anchor' | 'variant'> & {
  /**
   * Callback fired when the component requests to be closed.  Mirrors the onClose prop of the MuiDrawer component, but with the option of the `reason` being undefined to denote user involvement to close.
   */
  onClose: (e: object, reason?: 'backdropClick' | 'escapeKeyDown') => void
  /**
   * Expected to be an instance of `Heading`.
   */
  heading?: React.ReactNode
  footer?: React.ReactNode
  bodyPadding?: false
  /**
   * The maximum width of the drawer.  Only applied when the screen size is `md` or larger.
   */
  maxWidth?: number
  /**
   * Anchor the drawer to the `left` or `right` of the screen.  This component does not allow anchoring `top` or `bottom` like the MuiDrawer component does.
   */
  anchor?: 'right' | 'left'
}

const Drawer = (props: DrawerProps) => {
  const { anchor = 'left', heading, footer, bodyPadding, maxWidth, children, ...rest } = props
  const classes = useStyles({ bodyPadding, maxWidth })

  const modalProps: Partial<ModalProps> = rest.ModalProps ?? {}

  modalProps.BackdropProps ||= {}
  modalProps.BackdropProps.classes ||= {}
  modalProps.BackdropProps.classes.root = classes.drawerBackdrop

  return (
    <MuiDrawer
      {...rest}
      variant="temporary"
      anchor={anchor}
      classes={{
        paper: classes.drawerPaper,
      }}
      ModalProps={modalProps}
    >
      <div className={classes.drawerHeader}>
        {heading}
        <IconButton
          onClick={props.onClose}
          aria-label="Close"
        >
          <FontAwesomeIcon
            icon={faTimes as IconProp}
            className={classes.icon}
          />
        </IconButton>
      </div>
      <div className={classes.drawerBody}>
        <Box flexGrow={1}>{children}</Box>
        {footer && <div className={classes.footer}>{footer}</div>}
      </div>
    </MuiDrawer>
  )
}

export const Heading = (props: { component: ElementType } & ComponentProps<typeof Typography>) => {
  return (
    <Box color="white">
      <Typography
        {...props}
        color="inherit"
      />
    </Box>
  )
}

export default Drawer
